// Tee tiers -- a tee choice that spans every course.
//
// =====================================================================
// WHY
// A player's tee preference is not "White", it is a LEVEL they play from.
// The same level is called something different at each course:
//
//   Tier 1   Champions Pointe White   |   Covered Bridge Black
//   Tier 2   Champions Pointe Gray    |   Covered Bridge Gold
//
// So a player picks a tier once on the Roster tab and the app resolves the
// correct tee set automatically at whichever course they are playing that
// week. Adding a third course only needs its tees tagged with a tier.
// =====================================================================

// Every tier present in the tee data, ascending.
export function tiersFromTeeSets(teeSets = []) {
  const set = new Set();
  teeSets.forEach(t => {
    if (t.tee_tier !== null && t.tee_tier !== undefined && t.tee_tier !== '') {
      set.add(Number(t.tee_tier));
    }
  });
  return [...set].sort((a, b) => a - b);
}

/**
 * Human label for a tier, built from the tee colours that belong to it.
 * Tier 1 with Champions White + Covered Black reads "White / Black".
 * Derived rather than stored, so it stays correct when courses change.
 */
export function tierLabel(tier, teeSets = [], courses = []) {
  const members = teeSets
    .filter(t => Number(t.tee_tier) === Number(tier))
    .sort((a, b) => {
      const ca = courses.find(c => c.id === a.course_id)?.sort_order ?? 0;
      const cb = courses.find(c => c.id === b.course_id)?.sort_order ?? 0;
      return ca - cb;
    });
  if (members.length === 0) return `Tier ${tier}`;
  const names = [...new Set(members.map(t => t.name))];
  return names.join(' / ');
}

/** Longer label including the course each colour belongs to. */
export function tierLabelWithCourses(tier, teeSets = [], courses = []) {
  const members = teeSets.filter(t => Number(t.tee_tier) === Number(tier));
  if (members.length === 0) return `Tier ${tier}`;
  return members
    .map(t => {
      const c = courses.find(x => x.id === t.course_id);
      return `${c?.short_name || c?.name || t.course_id} ${t.name}`;
    })
    .join(' · ');
}

/** Dropdown options for a tier picker. */
export function tierOptions(teeSets = [], courses = []) {
  return tiersFromTeeSets(teeSets).map(tier => ({
    value: tier,
    label: tierLabel(tier, teeSets, courses),
    detail: tierLabelWithCourses(tier, teeSets, courses),
  }));
}

/** The tee set for a given tier at a given course, or null. */
export function teeSetForTierAtCourse(tier, courseId, teeSets = []) {
  if (tier === null || tier === undefined || tier === '') return null;
  return teeSets.find(
    t => t.course_id === courseId && Number(t.tee_tier) === Number(tier)
  ) || null;
}

/**
 * Does every course have a tee at this tier? A tier that is missing at one
 * course means players on it get silently bumped to a fallback tee there,
 * so the Courses tab warns about it.
 */
export function tierCoverageGaps(teeSets = [], courses = []) {
  const gaps = [];
  tiersFromTeeSets(teeSets).forEach(tier => {
    courses.forEach(c => {
      if (!teeSetForTierAtCourse(tier, c.id, teeSets)) {
        gaps.push({ tier, course: c });
      }
    });
  });
  return gaps;
}

/** Tee sets with no tier assigned -- unreachable as a default. */
export function untieredTeeSets(teeSets = []) {
  return teeSets.filter(
    t => t.tee_tier === null || t.tee_tier === undefined || t.tee_tier === ''
  );
}
